import { Header } from "@general-backoffice/core";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, Card, CardTitle, Col, Container, Row } from "reactstrap";

import { referrerEndpoints } from "../../../../api";
import useMarketCurrencyPairs from "../../../../components/market/hooks/useMarketCurrencyPairs";
import MarketCard from "../../../../components/market/MarketCard";
import { useDistributorWelcomeModal } from "../../../../components/modals/DistributorWelcomeModal";
import useNews from "../../../../components/news/hooks/useNews";
import NewsCard from "../../../../components/news/NewsCard";
import useReferrals from "../../../../components/referral/hooks/useReferrals";
import MonthlyStatsGraphCard from "../../../../components/referrer/partner/MonthlyStatsGraphCard";
import SummaryCard from "../../../../components/referrer/SummaryCard";
import useFetch from "../../../../hooks/useFetch";
import trustpilot from "../../../../media/misc/trustpilot.svg";
import type { RootState } from "../../../../redux/reducers/session/state";

interface YearStats {
  year?: number;
  paidOutComms?: number;
  uncollectedComms?: number;
  activeReferrals?: number;
  perMonth?: Array<{ month: number; paidOutComms: number; uncollectedComms: number; activeReferrals: number }>;
  [key: string]: unknown;
}

const prizeColors: Record<string, string> = {
  gold: "#FFD700",
  silver: "#C0C0C0",
  copper: "#CD7F32",
  default: "#6c757d",
};

const Home = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "distributorHome" });
  const user = useSelector((state: RootState) => state.sessionState.user);
  const { id, createdAt: signUpDate } = user || {};
  const { news, lastUpdate: lastUpdateNews, isLoading: isLoadingNews } = useNews();
  const { currencyPairs, lastUpdate: lastUpdateCurrency } = useMarketCurrencyPairs();

  const {
    isLoading: isLoadingActiveReferrals,
    pagination: { total: totalActiveReferrals },
  } = useReferrals({
    fields: [],
    filters: { pending: false, expired: false, claimed: false },
    pagination: { pageSize: 1 },
    mine: true,
  });

  // Onboarding modal (autoOpen if no activeReferrals and has less than 3 month)
  const { Modal: WelcomeModal, open: openWelcomeModal } = useDistributorWelcomeModal({
    autoOpen: !isLoadingActiveReferrals && !totalActiveReferrals && moment().diff(moment(signUpDate), "months") <= 3,
  }); //

  const { state: payoutWallet } = useFetch(() => referrerEndpoints.getPayoutsWallet().then(({ data }) => data));

  // Year stats
  const [year, setYear] = useState(moment().format("YYYY"));
  const { state: yearlyStats, isLoading: isLoadingStats } = useFetch(
    () => referrerEndpoints.getPartnerStats().then(({ data }) => data),
    { deps: [id] }
  );
  const currYearStats = useMemo(() => {
    if (!yearlyStats?.monthlyStats) return {};
    // Find monthly stats for the selected year
    const monthlyStats = Array.isArray(yearlyStats.monthlyStats) ? yearlyStats.monthlyStats : [];
    return monthlyStats.find((stats: YearStats) => parseInt(year) === stats?.year) || {};
  }, [yearlyStats, year]);

  const rank = useMemo(() => {
    if (totalActiveReferrals >= 30) return "gold";
    if (totalActiveReferrals >= 20) return "silver";
    if (totalActiveReferrals >= 10) return "copper";
    return null;
  }, [totalActiveReferrals]);
  return (
    <>
      <WelcomeModal />

      <Header.BreadCrumb
        color="secondary"
        isDark={false}
        title={t("title")}
        buttons={[
          {
            children: <i className="fas fa-info-circle"></i>,
            color: "default",
            size: "md",
            className: "shadow-none position-absolute",
            style: { transform: "translateY(-50%)" },
            onClick: () => openWelcomeModal(),
          },
        ]}
      >
        <Container fluid>
          <Row>
            {/* <Col xs={12}>
            <h2 className="display-3 my-5 text-center">
              Tu Panel de Distribuidor Investhinks
            </h2>
          </Col> */}

            <Col xs={{ size: 12 }} xl={{ size: 4, order: 1 }}>
              <Row>
                <Col xs={12}>
                  <SummaryCard
                    activeClients={totalActiveReferrals}
                    totalPendingPayouts={payoutWallet?.amount ?? 0}
                    totalCollectedPayouts={payoutWallet?.claimedAmount ?? 0}
                  />
                </Col>
                <Col xl={12} className="mb-4 mb-sm-0 mb-xl-4 d-none d-xl-block">
                  <Row className="flex-column flex-nowrap" style={{ height: "calc(100% - 30px)" }}>
                    <Col xs="auto" className="pb-2 w-100">
                      <NewsCard news={news} limitToShow={5} lastUpdated={lastUpdateNews} isLoading={isLoadingNews} />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>

            <Col xs={{ size: 12 }} xl={{ size: 8, order: 0 }}>
              <MonthlyStatsGraphCard
                year={Number(year)}
                setYear={(y: number) => setYear(String(y))}
                data={currYearStats}
                isLoading={isLoadingStats}
              />
              <Row>
                <Col xs={12} lg={6}>
                  <a href="https://es.trustpilot.com/review/investhinks.com" target="_blank" rel="noopener noreferrer">
                    <Button
                      color="success"
                      className="mb-3 bg-white w-100 text-center text-dark font-weight-400"
                      outline
                    >
                      <div className="mb--2">{t("trustpilot")}</div>
                      <img src={trustpilot} alt="Trustpilot" className="w-100" style={{ maxWidth: 200 }} />
                    </Button>
                  </a>
                  <MarketCard currencyPairs={currencyPairs} lastUpdated={lastUpdateCurrency} />
                </Col>
                <Col xs={12} lg={6}>
                  {/* Prize card */}
                  <Card className="mb-4 d-flex justify-content-center align-items-center flex-column px-3">
                    <CardTitle className="text-center mt-3">
                      <h3
                        className="mb-0 text-uppercase"
                        style={{ color: rank ? prizeColors[rank] : prizeColors.default }}
                      >
                        {rank}
                      </h3>
                      <p className="text-muted mb-0">
                        {/* Distribuidor destacado según el número de clientes activos referidos. */}
                        {t("currentRank", { rank: rank || t("noRank") })}
                      </p>
                    </CardTitle>
                    <div className="d-flex justify-content-center align-items-center mb-3">
                      <i
                        className="fas fa-trophy fa-5x"
                        style={{ color: rank ? prizeColors[rank] : prizeColors.default }}
                      ></i>
                    </div>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </Header.BreadCrumb>
    </>
  );
};

export default Home;
